import { useEffect, useState } from "react";
import { usePlan } from "../billing/PlanProvider";
import { useI18n } from "../i18n/I18nProvider";
import { buildUnifiedItinerary, resolveTripDayCity } from "../trips/tripItineraryModel";
import {
  listTripChecklist,
  listTripClimate,
  listTripExpenses,
  listTripHotels,
  type TripSummary,
} from "../trips/tripStore";
import { formatDate } from "../utils/format";
import { fetchAutomaticWeather, type AutomaticWeatherDay } from "../trips/weatherService";
import "./TripCommandCenter.css";

type CommandProps = {
  uid: string;
  trip: TripSummary;
  now: Date;
  dateFormat: Parameters<typeof formatDate>[1];
  openTrip: (target?: string) => void;
  openAi: () => void;
};

type TileKind = "transport" | "booking" | "weather" | "spending" | "reminder";

function Tile({ title, detail, secondary, action, onClick, icon, kind }: {
  title: string;
  detail: string;
  secondary?: string;
  action: string;
  onClick: () => void;
  icon: string;
  kind: TileKind;
}) {
  return (
    <article className={`ch-command-tile ch-command-tile--${kind}`}>
      <span className="ch-command-tile-head"><span className="ch-command-icon" aria-hidden="true">{icon}</span><span className="ch-command-label">{title}</span></span>
      <strong className="ch-command-value">{detail}</strong>
      {secondary && <span className="ch-command-caption">{secondary}</span>}
      <button type="button" className="ch-command-destination" onClick={onClick} aria-label={`${action}: ${title}`}><span>{action}</span></button>
    </article>
  );
}

export function TripCommandCenter({ uid, trip, now, dateFormat, openTrip, openAi }: CommandProps) {
  const { t, language } = useI18n();
  const { complete } = usePlan();
  const [remoteWeather, setRemoteWeather] = useState<{ tripId: string; day: AutomaticWeatherDay } | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherFailed, setWeatherFailed] = useState(false);
  const today = [now.getFullYear(), String(now.getMonth() + 1).padStart(2, "0"), String(now.getDate()).padStart(2, "0")].join("-");
  const clock = now.toTimeString().slice(0, 5);
  const date = (value: string) => value ? formatDate(`${value}T00:00:00`, dateFormat) : "";
  const future = (value: { date: string; startTime?: string }) =>
    value.date > today || (value.date === today && (!value.startTime || value.startTime >= clock));
  const itinerary = buildUnifiedItinerary(uid, trip).flatMap(day => day.entries);
  const transport = itinerary
    .filter(entry => (entry.sourceKind === "boardingPass" || entry.sourceKind === "transfer") && future(entry))
    .sort((a, b) => `${a.date}T${a.startTime || "00:00"}`.localeCompare(`${b.date}T${b.startTime || "00:00"}`))[0];
  const stays = listTripHotels(uid, trip.id).filter(item =>
    Boolean(item.checkIn) && (item.checkIn >= today || Boolean(item.checkOut && item.checkOut >= today))
  ).sort((a, b) => a.checkIn.localeCompare(b.checkIn));
  const stay = stays[0];
  const checklist = listTripChecklist(uid, trip.id);
  const pendingItems = checklist.filter(item => !item.completed);
  const reminder = pendingItems.filter(item => !item.date || item.date >= today)
    .sort((a, b) => (a.date || "9999-99-99").localeCompare(b.date || "9999-99-99"))[0];
  const forecastDay = trip.startDate && trip.startDate > today ? trip.startDate : today;
  // Weather forecasts have a strict horizon: Free 2 days, Complete 14 days.
  // Dates outside the horizon must never be labelled as current forecasts.
  const maxDate = new Date(Date.parse(`${today}T00:00:00Z`) + (complete ? 13 : 1) * 86400000).toISOString().slice(0, 10);
  const inTrip = !trip.startDate || forecastDay >= trip.startDate;
  const canForecast = inTrip && forecastDay <= maxDate && (!trip.endDate || forecastDay <= trip.endDate) && !trip.archivedAt;
  // Reuse the current itinerary, so city changes are reflected on the next workspace update.
  const weatherQuery = canForecast
    ? resolveTripDayCity(uid, trip, forecastDay)
    : "";
  useEffect(() => {
    let cancelled = false;
    setRemoteWeather(null);
    setWeatherFailed(false);
    if (!canForecast || !weatherQuery) { setWeatherLoading(false); return; }
    setWeatherLoading(true);
    void fetchAutomaticWeather({ requests: [{ date: forecastDay, query: weatherQuery }], language })
      .then(days => {
        if (cancelled) return;
        const day = days.find(item => item.date === forecastDay && item.query === weatherQuery);
        if (day) setRemoteWeather({ tripId: trip.id, day });
        else setWeatherFailed(true);
      }).catch(() => { if (!cancelled) setWeatherFailed(true); })
      .finally(() => { if (!cancelled) setWeatherLoading(false); });
    return () => { cancelled = true; };
  }, [uid, trip.id, forecastDay, canForecast, weatherQuery, language, complete]);
  const forecast = remoteWeather?.tripId === trip.id && remoteWeather.day.date === forecastDay && remoteWeather.day.query === weatherQuery
    ? remoteWeather.day : null;
  const climate = listTripClimate(uid, trip.id).find(item => item.date === forecastDay);
  const weatherDetail = forecast
    ? `${forecast.icon} ${t(forecast.conditionKey)} · ${Math.round(forecast.minTemp)}–${Math.round(forecast.maxTemp)} °C`
    : climate && !canForecast ? t("climate.autoUnavailable")
    : weatherLoading ? t("climate.loading")
    : climate ? [climate.conditions, climate.minTemp, climate.maxTemp].filter(Boolean).join(" · ")
    : t("climate.autoUnavailable");
  const weatherSecondary = forecast
    ? [forecast.resolvedName, date(forecastDay)].filter(Boolean).join(" · ")
    : climate && canForecast ? `${t("home.command.savedWeather")} · ${date(forecastDay)}`
    : weatherFailed ? t("climate.autoError") : undefined;
  const expenses = listTripExpenses(uid, trip.id);
  const totals = new Map<string, number>();
  for (const item of expenses) totals.set(item.currency, (totals.get(item.currency) || 0) + item.amount);
  const locale = language === "es" ? "es-ES" : language === "en" ? "en-GB" : "it-IT";
  const money = (amount: number, currency: string) => {
    try { return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount); }
    catch { return `${amount.toFixed(2)} ${currency}`; }
  };
  const totalsText = [...totals].map(([currency, amount]) => money(amount, currency)).join(" · ");
  const recent = expenses[0];
  return (
    <section className={`ch-command${stay ? "" : " ch-command--no-booking"}`} aria-label={t("home.command.title")}>
      <div className="ch-command-grid">
        <Tile kind="transport" icon="✈️" title={t("home.command.transport")}
          detail={transport?.title || t("home.command.transportEmpty")}
          secondary={transport ? [date(transport.date), transport.startTime, transport.notes].filter(Boolean).join(" · ") : undefined}
          action={t("trip.section.itinerary")} onClick={() => openTrip(transport ? `itinerary:${encodeURIComponent(transport.id)}` : "itinerary")}/>
        {stay && <Tile kind="booking" icon="🛏️" title={t("home.command.booking")}
          detail={stay.name}
          secondary={[stay.city, date(stay.checkIn)].filter(Boolean).join(" · ")}
          action={t("trip.section.hotels")} onClick={() => openTrip("hotels")}/>}

        <Tile kind="weather" icon="🌤️" title={t("home.command.weather") + " · " + new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(new Date(forecastDay + "T12:00:00"))}
          detail={weatherDetail}
          secondary={weatherSecondary}
          action={t("trip.section.weather")} onClick={() => openTrip("weather")}/>
        <Tile kind="spending" icon="💳" title={t("home.command.spending")}
          detail={totalsText || t("home.command.spendingEmpty")}
          secondary={recent ? `${t("home.command.recent")}: ${recent.title} · ${money(recent.amount, recent.currency)}` : undefined}
          action={t("trip.section.expenses")} onClick={() => openTrip("expenses")}/>
        <Tile kind="reminder" icon="🔔" title={t("home.command.reminder")}
          detail={reminder?.title || t("home.command.reminderEmpty")}
          secondary={reminder?.date ? date(reminder.date) : pendingItems.length ? t("home.command.pending", { count: String(pendingItems.length) }) : undefined}
          action={t("trip.section.checklist")} onClick={() => openTrip("checklist")}/>
      </div>
      <div className="ch-command-actions">
        <button type="button" className="ch-command-itinerary" onClick={() => openTrip("itinerary")}>
          <span className="ch-command-action-icon" aria-hidden="true">☷</span>
          <span>{t("home.command.reviewItinerary")}</span>
        </button>
        <button type="button" className="ch-command-agent" onClick={openAi}>
          <span className="ch-command-agent-icon" aria-hidden="true">✦</span>
          <span className="ch-command-agent-copy"><strong>{t("home.command.openAi")}</strong><small>{t("home.command.agentBody")}</small></span>
        </button>
      </div>
    </section>
  );
}
